/**
 * MobileSocialHint Component for LOOPINO AI Platform
 * Mobile-optimized Social Hint screen for browsing content suggestions
 */

import React, { useState, useEffect, useCallback } from 'react';
import './MobileSocialHint.css';
import MobileSocialHintDetail from './MobileSocialHintDetail.jsx';
import MobileSocialHintDetailImage from './MobileSocialHintDetailImage.jsx';
import { useVerticalSwipe } from '../../../hooks/useVerticalSwipe.js';
import useLanguage from '../../../hooks/useLanguage';

// Import icons
import iconSocialHint from '../../../assets/images/icons/icon-social-hint.svg';
import iconArrowRight from '../../../assets/images/icons/icon-arrow-right.svg';

const MobileSocialHint = ({
  onBack,
  currentView = 'list',
  selectedHintId = null
}) => {
  const { t } = useLanguage();

  // State management
  const [hints, setHints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [embedsLoading, setEmbedsLoading] = useState({});
  const [carouselStates, setCarouselStates] = useState({});

  const getLocalizedHints = useCallback(() => {
    const localizedHints = t('socialHint.list.mockHints', { returnObjects: true });
    if (Array.isArray(localizedHints)) {
      return localizedHints;
    }
    return [];
  }, [t]);

  // Load social hints (mock data for now)
  const loadSocialHints = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      const localizedHints = getLocalizedHints();
      setHints(localizedHints);

      // Initialize embed loading states and carousel states
      const embedLoadingStates = {};
      const carouselInitialStates = {};

      localizedHints.forEach(hint => {
        if (hint.embedUrls && hint.embedType === 'instagram') {
          // Initialize loading state for each embed
          hint.embedUrls.forEach((url, index) => {
            embedLoadingStates[`${hint.id}_${index}`] = true;
          });
          // Initialize carousel to first item
          carouselInitialStates[hint.id] = 0;
        }
      });

      setEmbedsLoading(embedLoadingStates);
      setCarouselStates(carouselInitialStates);
    } catch (err) {
      console.error('Error loading social hints:', err);
      const fallbackMessage = err?.message || t('common.error');
      setError(t('socialHint.list.errors.load', { error: fallbackMessage }));
    } finally {
      setIsLoading(false);
    }
  }, [getLocalizedHints, t]);

  // Load hints on component mount or language change
  useEffect(() => {
    loadSocialHints();
  }, [loadSocialHints]);

  // Handle hint click - seguendo esattamente il pattern di MobileAIStorage
  const handleHintClick = useCallback((hint) => {
    console.log('[MobileSocialHint] Hint clicked:', hint);
    // Determine which detail component to use based on hint ID
    const detailType = hint.id === 'hint_002' ? 'detail-image' : 'detail';
    if (onBack) {
      onBack(detailType, hint);
    }
  }, [onBack]);

  // Handle back navigation from detail
  const handleBackToList = useCallback(() => {
    if (onBack) {
      onBack('list');
    }
  }, [onBack]);

  // Handle back navigation from image detail
  const handleBackToListFromImage = useCallback(() => {
    if (onBack) {
      onBack('list');
    }
  }, [onBack]);

  // Handle vertical swipe navigation
  const handleSwipeUp = useCallback(() => {
    if (currentSlide < hints.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, hints.length]);

  const handleSwipeDown = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  // Initialize vertical swipe hook
  const verticalSwipe = useVerticalSwipe(handleSwipeUp, handleSwipeDown, {
    threshold: 50,
    velocityThreshold: 0.3,
    preventDefaultTouchmove: true
  });

  // Convert Instagram URL to embed URL
  const getInstagramEmbedUrl = (url) => {
    if (!url) return null;

    // Handle Instagram post URLs
    if (url.includes('instagram.com/p/')) {
      return `${url}embed/`;
    }

    // Handle Instagram reel URLs
    if (url.includes('instagram.com/reel/')) {
      return `${url}embed/`;
    }

    return url;
  };

  // Handle embed load
  const handleEmbedLoad = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle embed error
  const handleEmbedError = useCallback((embedKey) => {
    setEmbedsLoading(prev => ({
      ...prev,
      [embedKey]: false
    }));
  }, []);

  // Handle carousel navigation
  const handleCarouselNext = useCallback((hintId, maxIndex) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.min((prev[hintId] || 0) + 1, maxIndex)
    }));
  }, []);

  const handleCarouselPrev = useCallback((hintId) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: Math.max((prev[hintId] || 0) - 1, 0)
    }));
  }, []);

  // Handle carousel dot click
  const handleCarouselDotClick = useCallback((hintId, index) => {
    setCarouselStates(prev => ({
      ...prev,
      [hintId]: index
    }));
  }, []);

  // Handle horizontal swipe for carousel
  const handleCarouselTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    e.currentTarget.carouselStartX = touch.clientX;
    e.currentTarget.carouselStartY = touch.clientY;
    e.currentTarget.carouselStartTime = Date.now();
  }, []);

  const handleCarouselTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const startX = e.currentTarget.carouselStartX || touch.clientX;
    const startY = e.currentTarget.carouselStartY || touch.clientY;
    const deltaX = Math.abs(touch.clientX - startX);
    const deltaY = Math.abs(touch.clientY - startY);

    // If horizontal swipe is dominant, prevent vertical scroll
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleCarouselTouchEnd = useCallback((e, hintId, maxIndex) => {
    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const startX = e.currentTarget.carouselStartX || endX;
    const startY = e.currentTarget.carouselStartY || endY;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const deltaTime = Date.now() - (e.currentTarget.carouselStartTime || 0);

    console.log(`[Carousel ${hintId}] Touch end - deltaX: ${deltaX}, deltaY: ${deltaY}, time: ${deltaTime}ms`);

    // Only process if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      const minSwipeDistance = 30; // Reduced threshold for easier swipe
      const maxSwipeTime = 800; // Increased time for easier swipe

      if (Math.abs(deltaX) > minSwipeDistance && deltaTime < maxSwipeTime) {
        console.log(`[Carousel ${hintId}] Valid swipe detected: ${deltaX > 0 ? 'right' : 'left'}`);

        if (deltaX > 0) {
          // Swipe right - go to previous
          handleCarouselPrev(hintId);
        } else {
          // Swipe left - go to next
          handleCarouselNext(hintId, maxIndex);
        }
      }
    }
  }, [handleCarouselPrev, handleCarouselNext]);

  const getEmbedTypeLabel = useCallback((embedType) => {
    if (!embedType) {
      return '';
    }
    return t(`socialHint.common.embeds.types.${embedType}`, {
      defaultValue: t('socialHint.common.embeds.types.default')
    });
  }, [t]);

  const getIframeTitle = useCallback((index, title, embedType) => {
    const networkLabel = getEmbedTypeLabel(embedType) || embedType;
    return t('socialHint.common.embeds.iframeTitle', {
      index: index + 1,
      title,
      network: networkLabel
    });
  }, [getEmbedTypeLabel, t]);

  // Render hint card for full viewport slide
  const renderHintSlide = (hint) => (
    <div
      key={hint.id}
      className="mobile-social-hint-slide"
    >
      <div className="mobile-social-hint-single-container">
        <div
          className="mobile-social-hint-card"
          onClick={() => handleHintClick(hint)}
        >
          {/* Card title with icon */}
          <div className="mobile-social-hint-card-title-section">
            <div className="mobile-social-hint-card-title-left">
              <div className="mobile-social-hint-card-title-icon">
                <img src={iconSocialHint} alt={t('socialHint.common.alt.hintIcon')} />
              </div>
              <h2 className="mobile-social-hint-card-title-text">{hint.title}</h2>
            </div>
            <div className="mobile-social-hint-card-title-arrow">
              <img src={iconArrowRight} alt={t('socialHint.common.alt.arrow')} />
            </div>
          </div>

          <div className="mobile-social-hint-card-content">
            <p className="mobile-social-hint-description">
              {hint.detailedDescription || hint.description}
            </p>

            {/* Embed preview carousel */}
            <div className="mobile-social-hint-preview">
              {hint.embedUrls && hint.embedUrls.length > 0 && hint.embedType === 'instagram' ? (
                <div className="mobile-social-hint-carousel">
                  <div
                    className="mobile-social-hint-carousel-container"
                    style={{
                      transform: `translateX(-${(carouselStates[hint.id] || 0) * 100}%)`
                    }}
                  >
                    {hint.embedUrls.map((embedUrl, embedIndex) => {
                      const embedKey = `${hint.id}_${embedIndex}`;
                      return (
                        <div key={embedIndex} className="mobile-social-hint-carousel-slide">
                          <div className="mobile-social-hint-embed-container">
                            {embedsLoading[embedKey] && (
                              <div className="mobile-social-hint-embed-loading">
                                <div className="mobile-social-hint-spinner"></div>
                                <span>{t('socialHint.common.embeds.loading')}</span>
                              </div>
                            )}
                            <iframe
                              src={getInstagramEmbedUrl(embedUrl)}
                              className="mobile-social-hint-embed-iframe"
                              frameBorder="0"
                              scrolling="no"
                              allowtransparency="true"
                              onLoad={() => handleEmbedLoad(embedKey)}
                              onError={() => handleEmbedError(embedKey)}
                              title={getIframeTitle(embedIndex, hint.title, hint.embedType)}
                            />
                            {/* Touch overlay for swipe gestures */}
                            <div
                              className="mobile-social-hint-swipe-overlay"
                              onTouchStart={(e) => handleCarouselTouchStart(e)}
                              onTouchMove={(e) => handleCarouselTouchMove(e)}
                              onTouchEnd={(e) => handleCarouselTouchEnd(e, hint.id, hint.embedUrls.length - 1)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mobile-social-hint-embed-placeholder">
                  <div className="embed-preview-icon">
                    {hint.embedType === 'instagram' ? '📷' : '🎵'}
                  </div>
                  <div className="embed-preview-text">
                    <span className="embed-preview-type">{getEmbedTypeLabel(hint.embedType)}</span>
                    <span className="embed-preview-label">{t('socialHint.common.embeds.placeholder')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Carousel indicators outside content */}
            {hint.embedUrls && hint.embedUrls.length > 1 && (
              <div className="mobile-social-hint-external-indicators">
                {hint.embedUrls.map((_, dotIndex) => (
                  <button
                    key={dotIndex}
                    className={`mobile-social-hint-external-dot ${
                      (carouselStates[hint.id] || 0) === dotIndex ? 'active' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCarouselDotClick(hint.id, dotIndex);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div className="mobile-social-hint-empty-state">
      <div className="mobile-social-hint-empty-icon">
        <img src={iconSocialHint} alt={t('socialHint.common.alt.hintIcon')} />
      </div>
      <h3 className="mobile-social-hint-empty-title">{t('socialHint.list.empty.title')}</h3>
      <p className="mobile-social-hint-empty-description">
        {t('socialHint.list.empty.description')}
      </p>
    </div>
  );

  // Render loading state
  const renderLoadingState = () => (
    <div className="mobile-social-hint-loading">
      <div className="mobile-social-hint-loading-spinner">
        <div className="mobile-social-hint-spinner"></div>
      </div>
      <p>{t('socialHint.list.status.loading')}</p>
    </div>
  );

  // Render based on current view - seguendo esattamente il pattern di MobileAIStorage
  if (currentView === 'detail') {
    const selectedHint = hints.find(h => h.id === selectedHintId);
    return (
      <MobileSocialHintDetail
        hint={selectedHint}
        onBack={handleBackToList}
      />
    );
  }

  if (currentView === 'detail-image') {
    const selectedHint = hints.find(h => h.id === selectedHintId);
    return (
      <MobileSocialHintDetailImage
        hint={selectedHint}
        onBack={handleBackToListFromImage}
      />
    );
  }

  return (
    <div className="mobile-social-hint" {...verticalSwipe}>
      <div className="mobile-social-hint-content">
        {/* Error message */}
        {error && (
          <div className="mobile-social-hint-error">
            {error}
            <button
              className="mobile-social-hint-retry-btn"
              onClick={loadSocialHints}
            >
              {t('socialHint.common.actions.retry')}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="mobile-social-hint-main-content">
          {isLoading ? (
            renderLoadingState()
          ) : hints.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              {/* Slides container */}
              <div
                className="mobile-social-hint-slides-container"
                style={{
                  transform: `translateY(-${currentSlide * 100}vh)`
                }}
              >
                {hints.map(renderHintSlide)}
              </div>

              {/* Navigation indicators */}
              {hints.length > 1 && (
                <div className="mobile-social-hint-indicators">
                  {hints.map((_, index) => (
                    <div
                      key={index}
                      className={`mobile-social-hint-indicator ${index === currentSlide ? 'active' : ''}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileSocialHint;
